import { match } from 'react-router'

export type HistoryButtonsLocation = {
  key: string
  pathname: string
  noteId?: string
  filterId?: string
  channelId?: string
  templateId?: string
}

export type ShowRoutesParams = {
  noteId?: string
  channelId?: string
  filterId?: string
  templateId?: string
}

export type HistoryButtonsAction = {
  type: string
  payload: {
    match?: match<ShowRoutesParams> | null
    search?: string
  }
}

export type HistoryButtonsState = {
  index: number
  locations: HistoryButtonsLocation[]
}

export const initialState: HistoryButtonsState = {
  locations: [],
  index: -1,
}

// Internal helpers
function isSameLocation(state: HistoryButtonsState, key: string) {
  const location = state.locations[state.index]
  return location && location.key === key
}

function isNotesShowLocation(params: ShowRoutesParams) {
  return (
    Boolean(params.noteId) &&
    (Boolean(params.channelId) || Boolean(params.filterId))
  )
}

function isTemplatesShowLocation(params: ShowRoutesParams) {
  return Boolean(params.templateId)
}

function isChannelsShowLocation(params: ShowRoutesParams) {
  return Boolean(params.channelId) && !Boolean(params.noteId)
}

export const HistoryButtonsActionTypes = {
  PUSH_LOCATION: 'PUSH_LOCATION',
  CLEAR_HISTORY: 'CLEAR_HISTORY',
}

// Actions
export function pushLocation(
  match: match<ShowRoutesParams> | null,
  search: string
): HistoryButtonsAction {
  return {
    type: HistoryButtonsActionTypes.PUSH_LOCATION,
    payload: { match, search },
  }
}

export function clearHistory(): HistoryButtonsAction {
  return {
    type: HistoryButtonsActionTypes.PUSH_LOCATION,
    payload: {},
  }
}

// Selectors
export function getBackwardLocations(state: HistoryButtonsState) {
  return state.locations.slice(0, state.index)
}

export function getForwardLocations(state: HistoryButtonsState) {
  return state.locations.slice(state.index + 1)
}

export function getBackwardLocation(state: HistoryButtonsState) {
  const backwardLocations = getBackwardLocations(state)
  return backwardLocations[backwardLocations.length - 1]
}

export function getForwardLocation(state: HistoryButtonsState) {
  return getForwardLocations(state)[0]
}

export const reducer = (
  state = initialState,
  action: HistoryButtonsAction
): HistoryButtonsState => {
  switch (action.type) {
    case HistoryButtonsActionTypes.PUSH_LOCATION: {
      if (!action.payload.match) {
        return state
      }

      const {
        channelId,
        noteId,
        filterId,
        templateId,
      } = action.payload.match.params
      const pathname = action.payload.match.url
      const search = action.payload.search
      const key = noteId || templateId || channelId || 'root'

      // not on a note with context
      if (
        !isNotesShowLocation(action.payload.match.params) &&
        !isTemplatesShowLocation(action.payload.match.params) &&
        !isChannelsShowLocation(action.payload.match.params)
      ) {
        return state
      }

      // same
      if (isSameLocation(state, key)) {
        return state
      }

      // backward
      if (search === '?backward') {
        return {
          ...state,
          index: state.index - 1,
        }
      }

      // forward
      if (search === '?forward') {
        return {
          ...state,
          index: state.index + 1,
        }
      }

      const nextLocation: HistoryButtonsLocation = {
        key,
        pathname,
        channelId,
        filterId,
        noteId,
        templateId,
      }

      return {
        index: state.index + 1,
        locations: state.locations
          .slice(0, state.index + 1)
          .concat(nextLocation),
      }
    }
    case HistoryButtonsActionTypes.CLEAR_HISTORY: {
      return initialState
    }
    default: {
      return state
    }
  }
}

export default reducer
